/**
 * /moto-ecoles/:dept/:villeSlug — Page ville : liste complète + carte + FAQ + JSON-LD.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Award, Globe, ChevronRight, ArrowLeft, Home, ShieldCheck, HelpCircle, Info, ExternalLink, Phone, Sparkles, Flag, Calendar, Star, Megaphone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { API, SITE_NAME, useSeoMeta, formatDateFR, permisLabel } from './_shared';

const roseIcon = new L.DivIcon({
  className: 'c2r-marker',
  html: '<div style="width:22px;height:22px;background:#e11d48;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function MiniMap({ items, ville }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    // Cleanup safe (StrictMode double-mount protection)
    if (mapRef.current) {
      try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
      mapRef.current = null;
    }
    const points = items.filter(s => s.lat && s.lng);
    if (points.length === 0) return;
    const map = L.map(containerRef.current, {
      center: [points[0].lat, points[0].lng],
      zoom: 13,
      scrollWheelZoom: false,
      preferCanvas: true,
      zoomAnimation: false, // évite _leaflet_pos race
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    points.forEach(s => {
      L.marker([s.lat, s.lng], { icon: roseIcon })
        .bindPopup(`<div style="font-weight:800;font-size:12px">${s.nom}</div><div style="font-size:11px;color:#666">${s.adresse}</div>`)
        .addTo(map);
    });
    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map(s => [s.lat, s.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: false });
    }
    mapRef.current = map;
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
    };
  }, [items]);
  return <div ref={containerRef} className="rounded-2xl overflow-hidden border border-white/10" style={{ height: 340, background: '#0d0d0e' }} data-testid="ville-mini-map" />;
}

function SchoolCard({ school, dept, villeSlug }) {
  const gmaps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${school.nom} ${school.adresse} ${school.ville}`)}`;
  // Lien d'itinéraire direct (ouvre l'app Maps native sur mobile)
  const gmapsRoute = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${school.adresse}, ${school.code_postal} ${school.ville}`)}`;
  const isVerified = school.claim_status === 'verifiee' || school.claim_status === 'premium';
  const isEditorial = school.source_type === 'editorial';
  const isPartner = Boolean(school.featured_partner);
  const piste = school.piste || null;
  const gallery = (piste && Array.isArray(piste.gallery)) ? piste.gallery : [];
  const highlight = school.highlight || null;
  const reviews = school.google_reviews || null;
  const promo = school.promo_banner || null;
  // La bannière reste visible tant qu'elle existe en base. L'admin peut la
  // retirer manuellement ou mettre à jour la date de la prochaine session.
  const promoActive = Boolean(promo && promo.text);
  return (
    <article
      className={`rounded-2xl flex flex-col transition relative ${
        isPartner
          ? 'bg-gradient-to-br from-amber-500/[0.10] via-[#141416] to-[#141416] border-2 border-amber-400/50 hover:border-amber-300 shadow-[0_8px_32px_-12px_rgba(251,191,36,0.25)] overflow-hidden'
          : 'bg-[#141416] border border-white/10 hover:border-rose-500/30 hover:bg-[#181819] p-4 sm:p-5 gap-3'
      }`}
      data-testid={`ville-card-${school.id}`}
    >
      {/* Bannière promo (B96 2 avril 2026) — top de carte, rouge pulsant.
          Placée AVANT le padding pour border edge-to-edge. */}
      {isPartner && promoActive && (
        <a
          href={promo.cta_url || school.site_internet || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-red-600 via-red-500 to-red-600 border-b-2 border-red-400/40 hover:brightness-110 transition group"
          data-testid={`ville-card-promo-${school.id}`}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Megaphone className="w-4 h-4 text-white shrink-0 animate-pulse" />
            <div className="min-w-0">
              <div className="text-white font-black text-[12.5px] uppercase tracking-wide leading-tight">{promo.text}</div>
              {promo.subtitle && <div className="text-red-100 text-[10.5px] leading-tight">{promo.subtitle}</div>}
            </div>
          </div>
          {promo.cta_label && (
            <span className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-red-700 text-[10.5px] font-black uppercase tracking-widest group-hover:bg-red-50 transition">
              {promo.cta_label} →
            </span>
          )}
        </a>
      )}
      {/* Contenu principal avec padding — enveloppe tout le reste pour que
          la bannière soit edge-to-edge. */}
      <div className={isPartner ? "p-4 sm:p-6 flex flex-col gap-3" : "contents"}>

      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className={`font-black leading-tight ${isPartner ? 'text-amber-50 text-[17px] sm:text-[19px]' : 'text-white text-[15px] sm:text-[16px]'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {school.nom}
            {isVerified && <span className="ml-2 inline-flex items-center align-middle px-1.5 py-0.5 rounded bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[9px] font-black uppercase tracking-widest"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />Vérifiée</span>}
          </h3>
          <p className={`text-[11.5px] mt-1 flex items-start gap-1.5 ${isPartner ? 'text-amber-100/70' : 'text-white/50'}`}>
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="flex-1">
              {school.adresse}<br />{school.code_postal} {school.ville}
              <a
                href={gmapsRoute}
                target="_blank"
                rel="noopener noreferrer"
                className={`ml-2 inline-flex items-center gap-0.5 text-[10.5px] font-black uppercase tracking-wider underline underline-offset-2 ${
                  isPartner ? 'text-amber-200 hover:text-amber-100' : 'text-rose-300 hover:text-rose-200'
                } transition`}
                data-testid={`ville-card-itinerary-${school.id}`}
                title="Itinéraire sur Google Maps"
              >
                🧭 Itinéraire
              </a>
            </span>
          </p>
          {school.custom_phone && (
            <p className={`text-[13px] mt-2 inline-flex items-center gap-2 font-black ${isPartner ? 'text-amber-200' : 'text-white/70'}`}>
              <Phone className="w-3.5 h-3.5" />
              <a href={`tel:${school.custom_phone.replace(/\s/g, '')}`} className="hover:underline tabular-nums tracking-wider" data-testid={`ville-card-phone-${school.id}`}>
                {school.custom_phone}
              </a>
              {school.secondary_phone && (
                <span className="text-[11px] font-semibold opacity-70">
                  · urgence <a href={`tel:${school.secondary_phone.replace(/\s/g, '')}`} className="hover:underline tabular-nums">{school.secondary_phone}</a>
                </span>
              )}
            </p>
          )}
          {/* Note Opinion System (confiance partenaire) */}
          {isPartner && reviews && (
            <a
              href={reviews.url || school.site_internet || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-[11.5px] font-semibold text-amber-200/90 hover:text-amber-100 transition"
              data-testid={`ville-card-reviews-${school.id}`}
              title={`Note sur ${reviews.source || 'avis clients'}`}
            >
              <span className="inline-flex items-center gap-0.5">
                {[1,2,3,4,5].map(n => {
                  const pct = Math.max(0, Math.min(100, (reviews.rating_percent || 0) - (n-1)*20)) * 5;
                  return (
                    <span key={n} className="relative inline-block w-3.5 h-3.5">
                      <Star className="absolute inset-0 w-3.5 h-3.5 text-amber-500/30" strokeWidth={1.5} />
                      <span className="absolute inset-0 overflow-hidden" style={{ width: `${Math.min(pct, 100)}%` }}>
                        <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" strokeWidth={1.5} />
                      </span>
                    </span>
                  );
                })}
              </span>
              <span className="font-black">{reviews.rating_percent}% satisfaction</span>
              <span className="opacity-70">· {reviews.count} avis {reviews.source || ''}</span>
            </a>
          )}
          {!isPartner && school.custom_description && <p className="text-white/70 text-[12.5px] mt-2 line-clamp-3">{school.custom_description}</p>}
          {isEditorial && (
            <p className="mt-2 inline-flex items-start gap-1.5 text-[10px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Données éditoriales à confirmer par la Sécurité Routière · <em>vérifiées le {school.source_date || 'récemment'}</em>.</span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {isPartner ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/20 border border-amber-400/60 text-amber-100 text-[10px] font-black uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> Partenaire
            </span>
          ) : school.label_qualite ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest"><Award className="w-2.5 h-2.5" /> Qualité</span>
          ) : null}
          {isPartner && school.label_qualite && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest"><Award className="w-2.5 h-2.5" /> Qualité</span>
          )}
        </div>
      </header>

      {/* Encart piste privée (partenaire uniquement) — point fort visuel #1 */}
      {isPartner && piste && (
        <div className="rounded-xl bg-amber-400/10 border border-amber-400/40 p-3 sm:p-4 space-y-3" data-testid={`ville-card-piste-${school.id}`}>
          <div className="flex items-start gap-2.5">
            <Flag className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-amber-100 font-black text-[14px] leading-tight">
                {piste.name || 'Piste moto privée'}
                {piste.length_m && <span className="ml-1 text-amber-200/90">· {piste.length_m} m</span>}
              </div>
              {piste.address && (
                <div className="text-amber-200/70 text-[11px] mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {piste.address}
                </div>
              )}
            </div>
            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[9.5px] font-black uppercase tracking-widest">
              <Calendar className="w-2.5 h-2.5" /> Ouverte toute l'année
            </span>
          </div>
          {Array.isArray(piste.features) && piste.features.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-amber-50/90">
              {piste.features.map((f, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-amber-300 mt-0.5">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
          {/* Section permis dispensés — visuels officiels du site cer2lain.com */}
          <div className="pt-2 border-t border-amber-400/20">
            <div className="text-amber-100/90 text-[10.5px] font-black uppercase tracking-widest mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Permis dispensés dans cette agence
            </div>
          {/* Galerie officielle — ronds des permis dispensés par CER by Francine
              (visuels authentiques tirés du site cer2lain.com, pas de photo stock) */}
          {gallery.length > 0 && (
            <div className="grid grid-cols-4 gap-2 pt-1" data-testid={`ville-card-gallery-${school.id}`}>
              {gallery.slice(0, 4).map((img, i) => (
                <a
                  key={i}
                  href={school.site_internet || img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square rounded-xl overflow-hidden border border-amber-400/30 bg-white/95 p-2 flex flex-col items-center justify-center hover:border-amber-300 hover:bg-white transition"
                  data-testid={`ville-card-gallery-img-${school.id}-${i}`}
                  title={img.alt}
                >
                  <img
                    src={img.url}
                    alt={img.alt || 'Permis dispensé'}
                    loading="lazy"
                    className="max-w-full max-h-[70%] object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                  {img.caption && (
                    <span className="mt-1 text-stone-800 text-[9.5px] font-black uppercase tracking-wide text-center line-clamp-1">
                      {img.caption}
                    </span>
                  )}
                </a>
              ))}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Horaires d'ouverture — uniquement pour les partenaires (données officielles) */}
      {isPartner && school.opening_hours && (
        <div className="rounded-xl bg-white/[0.02] border border-amber-400/20 p-3" data-testid={`ville-card-hours-${school.id}`}>
          <div className="text-amber-100/90 text-[10.5px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> Horaires d'ouverture
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11.5px]">
            {['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'].map(day => {
              const slot = school.opening_hours[day];
              if (!slot) return null;
              const isClosed = /ferm/i.test(slot);
              return (
                <div key={day} className="flex items-center justify-between gap-2">
                  <span className="capitalize text-white/60">{day}</span>
                  <span className={`font-semibold ${isClosed ? 'text-red-300/80' : 'text-amber-100'}`}>{slot}</span>
                </div>
              );
            })}
          </div>
          {school.custom_email && (
            <div className="mt-2 pt-2 border-t border-amber-400/15 text-[11.5px]">
              <a href={`mailto:${school.custom_email}`} className="inline-flex items-center gap-1.5 text-amber-200 hover:text-amber-100 font-semibold" data-testid={`ville-card-email-${school.id}`}>
                ✉ {school.custom_email}
              </a>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {(school.permis || []).map(p => (
          <span key={p} className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
            isPartner
              ? (p === 'B' ? 'bg-blue-500/20 border border-blue-400/50 text-blue-100' : 'bg-amber-500/15 border border-amber-400/40 text-amber-100')
              : 'bg-rose-500/15 border border-rose-500/30 text-rose-200'
          }`}>{p}</span>
        ))}
        {typeof school.taux_reussite_moto === 'number' && (
          <span className={`ml-auto text-[11px] font-mono ${isPartner ? 'text-amber-200/80' : 'text-white/60'}`}>🏁 {Math.round(school.taux_reussite_moto * 100)}% réussite</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1 mt-auto">
        <a href={gmaps} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[11px] font-semibold transition ${
          isPartner
            ? 'border border-amber-400/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20'
            : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'
        }`} data-testid={`ville-card-map-${school.id}`}>
          <MapPin className="w-3.5 h-3.5" /> Voir sur la carte
        </a>
        {(school.custom_website || school.site_internet) && (
          <a href={(school.custom_website || school.site_internet).startsWith('http') ? (school.custom_website || school.site_internet) : `https://${school.custom_website || school.site_internet}`} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition ${
            isPartner
              ? 'bg-amber-500 hover:bg-amber-400 text-black'
              : 'bg-rose-600 hover:bg-rose-500 text-white'
          }`} data-testid={`ville-card-site-${school.id}`}>
            <Globe className="w-3.5 h-3.5" /> Site officiel
          </a>
        )}
        <Link to={`/moto-ecoles/${dept}/${villeSlug}/${school.id}/gerer`} className="ml-auto inline-flex items-center gap-1 h-9 px-2.5 rounded-xl border border-white/10 bg-white/[0.03] text-white/60 hover:text-white hover:bg-white/10 text-[10.5px] transition" data-testid={`ville-card-gerer-${school.id}`}>
          <Info className="w-3 h-3" /> Modifier / Retirer
        </Link>
      </div>
      </div>
    </article>
  );
}

export default function MotoEcolesVille() {
  const { dept, villeSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/annuaire/ville/${dept}/${villeSlug}`)
      .then(r => { if (!r.ok) throw new Error('not_found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [dept, villeSlug]);

  const villeTitle = data ? data.ville : villeSlug;
  const total = data?.total || 0;

  useSeoMeta({
    title: data ? `${total} moto-écoles à ${villeTitle} (${dept}) · Permis A, A1, A2 · ${SITE_NAME}` : `${villeSlug} · ${SITE_NAME}`,
    description: data ? `Trouve ta moto-école à ${villeTitle} (${dept}) parmi ${total} établissements agréés. Permis A, A1, A2 · Sources officielles Sécurité Routière · Mise à jour ${formatDateFR(data.updated_at)}.` : '',
    canonicalPath: `/moto-ecoles/${dept}/${villeSlug}`,
    jsonLd: data ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Moto-écoles à ${villeTitle}`,
      numberOfItems: total,
      itemListElement: (data.items || []).slice(0, 30).map((s, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'LocalBusiness',
          name: s.nom,
          address: {
            '@type': 'PostalAddress',
            streetAddress: s.adresse,
            postalCode: s.code_postal,
            addressLocality: s.ville,
            addressCountry: 'FR',
          },
          ...(s.lat && s.lng ? { geo: { '@type': 'GeoCoordinates', latitude: s.lat, longitude: s.lng } } : {}),
          ...(s.site_internet ? { url: s.site_internet.startsWith('http') ? s.site_internet : `https://${s.site_internet}` } : {}),
        },
      })),
    } : null,
  });

  if (loading) return <div className="min-h-screen bg-[#0a0a0b] text-white/50 p-8 text-sm">Chargement…</div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white p-8">
        <p className="text-white/60">Aucune moto-école recensée à cette adresse.</p>
        <Link to={`/moto-ecoles/${dept}`} className="mt-4 inline-flex items-center gap-2 text-rose-300 underline">← Retour au département</Link>
      </div>
    );
  }

  const permisDispo = Object.entries(data.permis_counts || {}).filter(([, n]) => n > 0);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-16" data-testid="moto-ecoles-ville">
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/moto-ecoles" className="hover:text-white">Annuaire</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/moto-ecoles/${dept}`} className="hover:text-white">{data.dept_name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">{villeTitle}</span>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Link to={`/moto-ecoles/${dept}`} className="inline-flex items-center gap-1 text-white/50 hover:text-white text-[12px] mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Toutes les villes du {dept}
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Moto-écoles à {villeTitle}
        </h1>
        <p className="text-white/60 mt-2 text-sm sm:text-base">
          <strong className="text-white">{total} établissement{total > 1 ? 's' : ''} agréé{total > 1 ? 's' : ''}</strong> à {villeTitle} · département {data.dept_name} ({dept}).
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {permisDispo.map(([p, n]) => (
            <span key={p} className="px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10.5px] font-black uppercase tracking-widest">
              {permisLabel(p)} · {n}
            </span>
          ))}
        </div>
      </header>

      {/* Carte */}
      {data.items.some(s => s.lat && s.lng) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-8">
          <MiniMap items={data.items} ville={villeTitle} />
        </section>
      )}

      {/* Partenaires officiels — section pleine largeur mise en avant.
          Ces écoles sortent de la grille classique pour disposer d'un vrai
          espace éditorial (galerie piste, point fort, coordonnées complètes). */}
      {(() => {
        const partners = (data.items || []).filter(s => s.featured_partner);
        const others = (data.items || []).filter(s => !s.featured_partner);
        return (
          <>
            {partners.length > 0 && (
              <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-6" data-testid="ville-partners-section">
                <h2 className="text-amber-200 text-[11px] font-black uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  Partenaire{partners.length > 1 ? 's' : ''} officiel{partners.length > 1 ? 's' : ''} à {villeTitle}
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  {partners.map(s => (
                    <SchoolCard key={s.id} school={s} dept={dept} villeSlug={villeSlug} />
                  ))}
                </div>
              </section>
            )}
            {others.length > 0 && (
              <section className="max-w-6xl mx-auto px-4 sm:px-6">
                {partners.length > 0 && (
                  <h2 className="text-white/60 text-[11px] font-black uppercase tracking-widest mb-3">
                    Autres moto-écoles à {villeTitle}
                  </h2>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {others.map(s => (
                    <SchoolCard key={s.id} school={s} dept={dept} villeSlug={villeSlug} />
                  ))}
                </div>
              </section>
            )}
          </>
        );
      })()}

      {/* Texte SEO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-12 space-y-4 text-white/70 text-[13.5px] leading-relaxed">
        <h2 className="text-white text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Passer son permis moto à {villeTitle}
        </h2>
        <p>
          À <strong>{villeTitle}</strong>, <strong>{total} moto-école{total > 1 ? 's' : ''}</strong> {total > 1 ? 'sont agréées' : 'est agréée'} pour dispenser la formation aux permis A, A1 et A2. Ces établissements figurent sur la liste officielle des écoles de conduite délivrée par la Sécurité Routière et publiée sur data.gouv.fr.
        </p>
        {data.label_qualite_count > 0 && (
          <p>
            <strong className="text-emerald-300"><Award className="inline w-3.5 h-3.5 mr-1" />{data.label_qualite_count} école{data.label_qualite_count > 1 ? 's' : ''}</strong> détient à ce jour le <em>Label Qualité des Formations aux Métiers de la Route</em>, gage d'engagement pédagogique et de sérieux dans l'apprentissage.
          </p>
        )}
        <p>
          Chaque fiche affiche les permis dispensés, l'adresse professionnelle et le site officiel. Aucune donnée personnelle du dirigeant n'est publiée.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
        <h2 className="text-white text-lg font-black mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          <HelpCircle className="w-4 h-4 text-rose-400" /> Questions fréquentes
        </h2>
        <Accordion type="single" collapsible className="space-y-2">
          <AccordionItem value="q1" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Comment sont sélectionnées les moto-écoles de {villeTitle} ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Nous reprenons intégralement la liste officielle des écoles agréées publiée par la <strong>Sécurité Routière</strong> sur data.gouv.fr. Aucune école n'est ajoutée ni retirée manuellement, sauf demande de retrait exercée dans le cadre du droit d'opposition RGPD.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q2" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Que signifie le badge « Label Qualité » ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Le <em>Label Qualité des Formations aux Métiers de la Route</em> est décerné par l'État aux écoles répondant à un cahier des charges pédagogique et administratif exigeant (formation continue des moniteurs, transparence tarifaire, suivi des élèves).</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q3" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Je suis gérant, comment modifier ma fiche ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Cliquez sur le bouton « Modifier / Retirer » de votre fiche : vous pourrez la <strong>revendiquer gratuitement</strong> (SIRET + email pro), demander une correction, ou exercer votre <strong>droit d'opposition RGPD</strong> pour retirer votre établissement de l'annuaire.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q4" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Quelle est la différence entre les permis A, A1 et A2 ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Le <strong>permis A1</strong> autorise la conduite d'une 125 cm³ dès 16 ans. Le <strong>permis A2</strong> (dès 18 ans) permet de rouler avec une moto limitée à 35 kW pendant 2 ans. Le <strong>permis A</strong> (dès 20 ans avec passerelle A2 → A) donne accès à toutes les cylindrées.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Source */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-white/5 text-[11.5px] text-white/40 space-y-1">
        <p>
          <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
          <strong className="text-white/60">Source</strong> : Sécurité Routière · <a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-300">data.gouv.fr</a>
          {data.updated_at && <> · <strong className="text-white/60">Dernière vérification</strong> : {formatDateFR(data.updated_at)}</>}.
        </p>
        <p>
          <Link to="/mentions-annuaire" className="underline hover:text-rose-300">Mentions légales &amp; RGPD</Link> · Aucune donnée personnelle du dirigeant n'est affichée.
        </p>
      </section>
    </div>
  );
}
